import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { UserTourProgress, TourEventType } from "../types";

const QK = (uid?: string) => ["tour-progress", uid ?? "anon"];

/**
 * Loads + persists per-user tour progress against `user_tour_progress`.
 * Auto-creates a row if missing (defensive, in case the trigger missed it).
 */
export const useTourProgress = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userId = user?.id;

  const query = useQuery({
    queryKey: QK(userId),
    enabled: !!userId,
    queryFn: async (): Promise<UserTourProgress | null> => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from("user_tour_progress")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;

      if (data) return data as unknown as UserTourProgress;

      // Defensive: create if missing
      const { data: created, error: insertError } = await supabase
        .from("user_tour_progress")
        .insert({ user_id: userId })
        .select("*")
        .single();

      if (insertError) throw insertError;
      return created as unknown as UserTourProgress;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (patch: Partial<UserTourProgress>) => {
      if (!userId) throw new Error("Not authenticated");

      const safePatch = { ...patch, last_seen_at: new Date().toISOString() } as never;

      const { data, error } = await supabase
        .from("user_tour_progress")
        .update(safePatch)
        .eq("user_id", userId)
        .select("*")
        .single();

      if (error) throw error;
      return data as unknown as UserTourProgress;
    },
    onSuccess: (data) => {
      qc.setQueryData(QK(userId), data);
    },
  });

  const logEvent = async (
    stepId: string,
    eventType: TourEventType,
    extra?: { selected_option_id?: string; page_path?: string; metadata?: Record<string, unknown> },
  ) => {
    if (!userId) return;
    await supabase.from("user_tour_events").insert({
      user_id: userId,
      step_id: stepId,
      event_type: eventType,
      selected_option_id: extra?.selected_option_id,
      page_path: extra?.page_path,
      metadata: extra?.metadata as never,
    });
  };

  // Touch last_seen_at on first load
  useEffect(() => {
    if (query.data && userId) {
      supabase
        .from("user_tour_progress")
        .update({ last_seen_at: new Date().toISOString() })
        .eq("user_id", userId)
        .then(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, query.data?.id]);

  return {
    progress: query.data ?? null,
    isLoading: query.isLoading,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    logEvent,
    refetch: query.refetch,
  };
};
